// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useState, useCallback } from 'react'
import { Activity, Users, LogIn, Building2, RefreshCw, CircleAlert, Wifi, Gauge, Timer, AlertTriangle, Database, Zap } from 'lucide-react'
import * as api from '../../api/client'
import { useAutoRefresh } from '../../components/useAutoRefresh'
import { timeAgo } from '../../lib/opsFormat'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

const WINDOWS = [5, 15, 30, 60]

// D-UI-2: what each column of the two tables shows, for its header funnel.
const LOAD_FUNNELS: FunnelColumn<api.ActiveSessionsResponse['by_pharmacy'][number]>[] = [
  { key: 'pharmacy', label: 'Pharmacy',   value: p => p.pharmacy_name },
  { key: 'users',    label: 'Users',      value: p => p.users },
  { key: 'sessions', label: 'Sessions',   value: p => p.active_sessions },
  { key: 'online',   label: 'Online now', value: p => p.online_now },
]
const SESSION_FUNNELS: FunnelColumn<api.ActiveSessionsResponse['sessions'][number]>[] = [
  { key: 'user',     label: 'User',      value: s => s.display_name || s.email },
  { key: 'pharmacy', label: 'Pharmacy',  value: s => s.pharmacy_name },
  { key: 'role',     label: 'Role',      value: s => s.role },
  { key: 'status',   label: 'Status',    value: s => s.online ? 'Online' : 'Idle' },
  { key: 'seen',     label: 'Last seen', value: s => s.last_seen_at },
  { key: 'signed',   label: 'Signed in', value: s => s.created_at },
  { key: 'ip',       label: 'IP',        value: s => s.ip },
]

/**
 * Live Sessions — platform-wide "who is on right now" for SaaS operators. Reads /ops/active-sessions
 * (cross-tenant). Surfaces concurrency/load (the in-app proxy for demand) and points to Application
 * Insights / Azure SQL for true latency / error-rate / DTU metrics.
 */
export default function LiveSessionsTab() {
  const [data, setData] = useState<api.ActiveSessionsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null)
  const [windowMin, setWindowMin] = useState(5)
  const [auto, setAuto] = useState(true)
  // Performance panel reads /ops/dashboard (Azure Monitor — Function App latency/throughput/5xx +
  // SQL tier). 24h-windowed and slow/cached upstream, so it loads on mount + manual refresh only,
  // not on the 20s session interval. Best-effort: never blocks the session view.
  const [perf, setPerf] = useState<api.OpsDashboard | null>(null)
  const [perfLoading, setPerfLoading] = useState(true)

  const load = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true)
    setErr(null)
    try {
      const d = await api.getActiveSessions(windowMin)
      setData(d); setUpdatedAt(new Date())
    } catch (e: any) { setErr(e?.message || 'Failed to load active sessions') }
    finally { setLoading(false); setRefreshing(false) }
  }, [windowMin])

  const loadPerf = useCallback(async () => {
    setPerfLoading(true)
    try { setPerf(await api.getOpsDashboard(1)) } catch { /* perf is best-effort */ }
    finally { setPerfLoading(false) }
  }, [])

  useEffect(() => { load(); loadPerf() }, [load, loadPerf])
  // live: refresh every 20s while the toggle is on (86cb8uk5f: gated + one tab per browser)
  useAutoRefresh(() => load(true), { intervalMs: 20_000, active: auto, leaderKey: `ops.live-sessions:${windowMin}` })

  // The header funnels filter the whole loaded set of each table.
  const loadFx = useTableFunnels(data?.by_pharmacy, LOAD_FUNNELS)
  const sessFx = useTableFunnels(data?.sessions, SESSION_FUNNELS)

  const s = data?.summary
  const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0)

  // Azure Monitor perf metrics — which set populates depends on the Function App plan, so render
  // whichever names came back. All best-effort; absent => tile omitted.
  const ap = perf?.azure_performance
  const metrics = ap?.metrics || []
  const findM = (n: string) => metrics.find(m => m.name === n)
  const mRt = findM('HttpResponseTime')
  const mReq = findM('Requests')
  const m5xx = findM('Http5xx')
  const mExec = findM('FunctionExecutionCount')
  const tier = perf?.azure_sql_tier
  const errRate = mReq && m5xx && mReq.sum > 0 ? (m5xx.sum / mReq.sum) * 100 : null
  const rtSeconds = mRt ? String(mRt.unit || '').toLowerCase().startsWith('sec') : false
  const rtVal = mRt ? (rtSeconds ? Math.round(mRt.avg * 1000) : Math.round(mRt.avg)) : null
  const rtUnit = mRt ? (rtSeconds ? 'ms' : (mRt.unit || '')) : ''
  const dtuBased = ['Basic', 'Standard', 'Premium'].includes(tier?.tier || '')
  const hasPerfTiles = !!(mRt || mReq || m5xx || mExec || tier?.available)

  return (
    <div style={S.page}>
      <div style={S.head}>
        <div>
          <div style={S.eyebrow}>OWL CONSULTATIONS · LIVE ACTIVITY</div>
          <h1 style={S.h1}>Live Sessions</h1>
          <div style={S.subtitle}>Who is on the platform right now — across every pharmacy.</div>
        </div>
        <div style={S.headRight}>
          <label style={S.windowSel}>
            Online window
            <select value={windowMin} onChange={e => setWindowMin(Number(e.target.value))} style={S.select}>
              {WINDOWS.map(w => <option key={w} value={w}>{w} min</option>)}
            </select>
          </label>
          <label style={S.autoLbl}>
            <input type="checkbox" checked={auto} onChange={e => setAuto(e.target.checked)} /> Auto
          </label>
          {updatedAt && <span style={S.updated}>Updated {updatedAt.toLocaleTimeString()}</span>}
          <button onClick={() => { load(true); loadPerf() }} disabled={refreshing} style={S.refreshBtn} aria-label="Refresh">
            <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : undefined }} /> Refresh
          </button>
        </div>
      </div>

      {err && <div style={S.errorBanner}><CircleAlert size={16} />{err}</div>}

      {loading ? (
        <div style={S.loading}><RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} /> Loading live sessions…</div>
      ) : !data || !s ? null : (
        <>
          <div style={S.cards}>
            <Metric icon={<Wifi size={18} />} accent="#10b981"
              value={s.online_now_users} label="Users online now"
              sub={`${s.online_now_sessions} session${s.online_now_sessions === 1 ? '' : 's'} · active in last ${data.online_window_minutes}m`} />
            <Metric icon={<Users size={18} />} accent="#0891b2"
              value={s.active_users} label="Signed-in users"
              sub={`${pct(s.active_users, s.total_active_users)}% of ${s.total_active_users} accounts · ${s.active_sessions} live sessions`} />
            <Metric icon={<LogIn size={18} />} accent="#7c3aed"
              value={s.logins_last_hour} label="Logins last hour" sub="New sessions in the last 60 min" />
            <Metric icon={<Building2 size={18} />} accent="#d97706"
              value={s.pharmacies_with_activity} label="Pharmacies active" sub={`of ${s.total_pharmacies} total`} />
          </div>

          {s.capped && <div style={S.capNote}>Showing the 500 most-recently-active sessions (there are more). Counts reflect that cap.</div>}

          <section style={S.card}>
            <div style={S.cardTitle}><Building2 size={15} /> Load by pharmacy</div>
            {data.by_pharmacy.length === 0 ? <div style={S.empty}>No active sessions.</div> : (
              <>
              {loadFx.chips}
              <table {...loadFx.tableProps} style={S.table}>
                <thead><tr>
                  <th style={S.th}>Pharmacy {loadFx.funnel('pharmacy')}</th><th style={S.thR}>Users {loadFx.funnel('users')}</th>
                  <th style={S.thR}>Sessions {loadFx.funnel('sessions')}</th><th style={S.thR}>Online now {loadFx.funnel('online')}</th>
                </tr></thead>
                <tbody>
                  {loadFx.rows.map((p, i) => (
                    <tr key={p.pharmacy_id ?? `u${i}`} style={S.tr}>
                      <td style={S.td}>{p.pharmacy_name}</td>
                      <td style={S.tdR}>{p.users}</td>
                      <td style={S.tdR}>{p.active_sessions}</td>
                      <td style={S.tdR}>{p.online_now > 0 ? <span style={S.onlinePill}>{p.online_now}</span> : <span style={S.zero}>0</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </>
            )}
          </section>

          <section style={S.card}>
            <div style={S.cardTitle}><Activity size={15} /> Active sessions <span style={S.countBadge}>{data.sessions.length}</span></div>
            {data.sessions.length === 0 ? <div style={S.empty}>No active sessions.</div> : (
              <>
              {sessFx.chips}
              <div style={{ overflowX: 'auto' }}>
                <table {...sessFx.tableProps} style={S.table}>
                  <thead><tr>
                    <th style={S.th}>User {sessFx.funnel('user')}</th><th style={S.th}>Pharmacy {sessFx.funnel('pharmacy')}</th><th style={S.th}>Role {sessFx.funnel('role')}</th>
                    <th style={S.th}>Status {sessFx.funnel('status')}</th><th style={S.th}>Last seen {sessFx.funnel('seen')}</th><th style={S.th}>Signed in {sessFx.funnel('signed')}</th><th style={S.th}>IP {sessFx.funnel('ip')}</th>
                  </tr></thead>
                  <tbody>
                    {sessFx.rows.map(sess => (
                      <tr key={sess.id} style={S.tr}>
                        <td style={S.td}>
                          <div style={S.userName}>{sess.display_name || sess.email || '—'}</div>
                          {sess.display_name && sess.email && sess.display_name !== sess.email && <div style={S.userEmail}>{sess.email}</div>}
                        </td>
                        <td style={S.td}>{sess.pharmacy_name || <span style={S.zero}>—</span>}</td>
                        <td style={S.td}>{sess.role || <span style={S.zero}>—</span>}</td>
                        <td style={S.td}><span style={{ ...S.dot, background: sess.online ? '#10b981' : '#cbd5e1' }} />{sess.online ? 'Online' : 'Idle'}</td>
                        <td style={S.td}>{timeAgo(sess.last_seen_at)}</td>
                        <td style={S.td}>{timeAgo(sess.created_at)}</td>
                        <td style={S.tdMono}>{sess.ip || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </section>

          <section style={{ ...S.card, ...S.perfCard }}>
            <div style={S.cardTitle}><Gauge size={15} /> Performance &amp; health
              {ap?.available && <span style={S.perfWindow}>API · last {ap.window || '24h'}</span>}
            </div>

            {perfLoading ? (
              <div style={S.perfMsg}><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading platform metrics…</div>
            ) : hasPerfTiles ? (
              <div style={S.perfGrid}>
                {mRt && <PerfTile icon={<Timer size={16} />} accent="#0891b2" value={String(rtVal)} unit={rtUnit} label="Avg response time" />}
                {mReq && <PerfTile icon={<Zap size={16} />} accent="#7c3aed" value={mReq.sum.toLocaleString()} label="Requests (24h)" />}
                {m5xx && <PerfTile icon={<AlertTriangle size={16} />} accent={m5xx.sum > 0 ? '#dc2626' : '#10b981'} value={m5xx.sum.toLocaleString()} unit={errRate != null ? `${errRate.toFixed(2)}%` : ''} label="5xx errors (24h)" />}
                {mExec && <PerfTile icon={<Activity size={16} />} accent="#0891b2" value={mExec.sum.toLocaleString()} label="Executions (24h)" />}
                {tier?.available && <PerfTile icon={<Database size={16} />} accent="#d97706" value={tier.capacity != null ? String(tier.capacity) : (tier.sku || '—')} unit={tier.capacity != null ? (dtuBased ? 'DTU' : 'vCore') : ''} label={`DB tier · ${tier.compute || tier.tier || ''}`} />}
              </div>
            ) : (
              <div style={S.perfUnavail}>
                Live Azure metrics aren’t available yet{ap?.reason ? ` — ${ap.reason}` : ''}. They light up once the Function App’s managed
                identity has <strong>Monitoring Reader</strong> (and <strong>Reader</strong> on the SQL database) at the resource-group level.
              </div>
            )}

            <p style={S.perfP}>
              The cards above are the platform-telemetry view (Azure Monitor): response time, request volume and error count for the API,
              plus the database tier behind throughput. The session metrics at the top of this tab are the live <strong>concurrency / load</strong>
              signal. For the deep dive — p50/p95/p99 by operation, real-time RPS and the full failure breakdown — open
              <strong> Application Insights</strong> (Live Metrics / Performance / Failures) and <strong>Azure SQL</strong> (CPU / DTU) in the portal.
            </p>
          </section>
        </>
      )}
    </div>
  )
}

function Metric({ icon, accent, value, label, sub }: { icon: React.ReactNode; accent: string; value: number; label: string; sub: string }) {
  return (
    <div style={S.metric}>
      <div style={{ ...S.metricIcon, color: accent, background: accent + '14' }}>{icon}</div>
      <div style={S.metricVal}>{value.toLocaleString()}</div>
      <div style={S.metricLabel}>{label}</div>
      <div style={S.metricSub}>{sub}</div>
    </div>
  )
}

function PerfTile({ icon, accent, value, unit, label }: { icon: React.ReactNode; accent: string; value: string; unit?: string; label: string }) {
  return (
    <div style={S.perfTile}>
      <div style={{ ...S.perfTileIcon, color: accent, background: accent + '14' }}>{icon}</div>
      <div style={S.perfTileVal}>{value}{unit ? <span style={S.perfTileUnit}> {unit}</span> : null}</div>
      <div style={S.perfTileLabel}>{label}</div>
    </div>
  )
}

const S: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 18 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' },
  eyebrow: { fontSize: 11, fontWeight: 700, letterSpacing: 1, color: '#0891b2' },
  h1: { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '2px 0 0' },
  subtitle: { fontSize: 13, color: '#64748b', marginTop: 2 },
  headRight: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  windowSel: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b' },
  select: { fontSize: 12, padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 6, color: '#0f172a' },
  autoLbl: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#64748b' },
  updated: { fontSize: 12, color: '#94a3b8' },
  refreshBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', fontSize: 13, fontWeight: 600, color: '#0e7490', background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: 8, cursor: 'pointer' },
  errorBanner: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: 13 },
  loading: { display: 'flex', alignItems: 'center', gap: 10, padding: 40, color: '#64748b', fontSize: 14, justifyContent: 'center' },

  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 },
  metric: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 },
  metricIcon: { width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 },
  metricVal: { fontSize: 28, fontWeight: 700, color: '#0f172a', lineHeight: 1 },
  metricLabel: { fontSize: 13, fontWeight: 600, color: '#334155', marginTop: 6 },
  metricSub: { fontSize: 12, color: '#94a3b8', marginTop: 2 },
  capNote: { fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px' },

  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 },
  cardTitle: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 12 },
  countBadge: { fontSize: 12, fontWeight: 600, color: '#0e7490', background: '#ecfeff', borderRadius: 999, padding: '2px 9px' },
  empty: { fontSize: 13, color: '#94a3b8', padding: '8px 0' },

  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: '#94a3b8', padding: '6px 10px', borderBottom: '1px solid #e2e8f0' },
  thR: { textAlign: 'right', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: '#94a3b8', padding: '6px 10px', borderBottom: '1px solid #e2e8f0' },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '9px 10px', color: '#334155', verticalAlign: 'top' },
  tdR: { padding: '9px 10px', color: '#334155', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  tdMono: { padding: '9px 10px', color: '#64748b', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 },
  userName: { fontWeight: 600, color: '#0f172a' },
  userEmail: { fontSize: 12, color: '#94a3b8' },
  dot: { display: 'inline-block', width: 8, height: 8, borderRadius: 999, marginRight: 7, verticalAlign: 'middle' },
  onlinePill: { display: 'inline-block', minWidth: 22, textAlign: 'center', fontWeight: 700, color: '#047857', background: '#d1fae5', borderRadius: 999, padding: '1px 8px' },
  zero: { color: '#cbd5e1' },

  perfCard: { background: '#f8fafc' },
  perfP: { fontSize: 13, color: '#475569', lineHeight: 1.55, margin: '10px 0 0' },
  perfWindow: { marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: '#94a3b8' },
  perfMsg: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#64748b', padding: '6px 0' },
  perfGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 },
  perfUnavail: { fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', lineHeight: 1.5 },
  perfTile: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' },
  perfTileIcon: { width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 },
  perfTileVal: { fontSize: 22, fontWeight: 700, color: '#0f172a', lineHeight: 1 },
  perfTileUnit: { fontSize: 13, fontWeight: 600, color: '#94a3b8' },
  perfTileLabel: { fontSize: 12, color: '#64748b', marginTop: 4 },
}
